// Barra de ferramentas do escritório (como no Habblaud): dois grupos de botões quadrados no canto
// inferior direito do campus. Grupo "vista": Visão geral e zoom. Grupo "painéis": Meu dia,
// Entregas, Histórico, Timelapse, Terminal, tema da tela e Ajuda.
//
// Outros módulos registram as suas com registerTool(). O registro é um Map em memória e só vira
// DOM em refreshTools(), que o main chama a cada renderização. Atalhos: uma escuta única no
// document, que ignora Ctrl/Alt/Meta e campos de texto. Em tela estreita (até 640 px) a barra
// vira uma linha rolável no rodapé. Nada aqui roda na importação: as funções puras são testáveis.

import { onLangChange, t } from './i18n.js';

export type GrupoFerramenta = 'vista' | 'paineis';

export interface Ferramenta {
  id: string;
  /** SVG inline (viewBox 24). A barra dimensiona e pinta pelo CSS. */
  icon: string;
  /** Nome já traduzido: vai ao tooltip e ao aria-label. */
  label: string;
  /** Tecla que aciona a ferramenta (O, M, +...). Aparece entre parênteses no tooltip. */
  shortcut?: string;
  group: GrupoFerramenta;
  onClick: () => void;
  /** Botão aceso (fundo azul): o painel dela está aberto. */
  active?: () => boolean;
  /** Selo de contagem (Entregas): 0 esconde; acima de 9 mostra "9+". */
  badge?: () => number;
}

/** O que a barra precisa do resto da interface (main.ts e a câmera do campus). */
export interface AcoesBarra {
  campus: { fitAll(): void; zoomIn(): void; zoomOut(): void };
  /** Painel aberto agora, com os nomes de main.ts ('day', 'terminal', 'bridge' ou null). */
  painel: () => string | null;
  terminal: () => void;
  timelapse: () => void;
  timelapseAberto: () => boolean;
  ajudaAberta: () => boolean;
  ajuda: () => void;
}

const ferramentas = new Map<string, Ferramenta>();
/** Botão no DOM de cada ferramenta; `ferramenta` é o registro que ele está desenhando. */
const botoes = new Map<string, { botao: HTMLButtonElement; ferramenta: Ferramenta }>();
/** Ferramentas que a própria barra registrou (as de montaBarra), para trocar o rótulo ao mudar de idioma. */
const minhas = new Map<string, Ferramenta>();
let barra: HTMLElement | undefined;
let acoesMontadas: AcoesBarra | undefined;
const grupos = new Map<GrupoFerramenta, HTMLElement>();

/** Registra uma ferramenta; com o mesmo id, troca a anterior (a posição continua a da primeira vez). */
export function registerTool(ferramenta: Ferramenta): void {
  const selo = selos.get(ferramenta.id);
  ferramentas.set(ferramenta.id, selo && !ferramenta.badge ? { ...ferramenta, badge: selo } : ferramenta);
  refreshTools();
}

/** Selos pedidos por outros módulos (ex.: Entregas) para um botão que a barra já registra. */
const selos = new Map<string, () => number>();

/** Liga um selo de contagem a um botão da barra (vale também se o botão for registrado depois). */
export function attachBadge(id: string, badge: () => number): void {
  selos.set(id, badge);
  const atual = ferramentas.get(id);
  if (atual) ferramentas.set(id, { ...atual, badge });
  refreshTools();
}

/** Redesenha o estado dos botões: aceso, selo e, se o registro mudou, rótulo e ícone. Sem barra montada, não faz nada. */
export function refreshTools(): void {
  if (!barra) return;
  for (const ferramenta of ferramentas.values()) {
    let entrada = botoes.get(ferramenta.id);
    if (!entrada) {
      entrada = { botao: criaBotao(ferramenta.id), ferramenta };
      desenhaRotulo(entrada.botao, ferramenta);
      botoes.set(ferramenta.id, entrada);
      (grupos.get(ferramenta.group) ?? grupos.get('paineis'))?.append(entrada.botao);
    }
    if (entrada.ferramenta !== ferramenta) {
      entrada.ferramenta = ferramenta;
      desenhaRotulo(entrada.botao, ferramenta);
    }
    pintaEstado(entrada.botao, ferramenta);
  }
  for (const ouvinte of ouvintesBarra) ouvinte();
}

/** Ferramenta registrada com este id (a versão de celular usa o ícone, o selo e a ação). */
export function ferramentaDaBarra(id: string): Ferramenta | undefined {
  return ferramentas.get(id);
}

const ouvintesBarra = new Set<() => void>();

/** Avisa quando a barra redesenha (selos, estado aceso): a versão de celular atualiza as abas. */
export function ouvirBarra(ouvinte: () => void): void {
  ouvintesBarra.add(ouvinte);
}

/** Texto do selo: vazio esconde; acima de 9 vira "9+". */
export function textoSelo(contagem: number): string {
  if (contagem <= 0) return '';
  return contagem > 9 ? '9+' : String(contagem);
}

/** Normaliza uma tecla para comparar: letras sem caixa, "=" conta como "+" e "−" (U+2212) como "-". */
export function normalizaTecla(tecla: string): string {
  if (tecla === '=') return '+';
  if (tecla === '−') return '-';
  return tecla.length === 1 ? tecla.toLowerCase() : tecla;
}

/** A ferramenta que a tecla aciona, se houver (atalho sem caixa; o = vale como +). */
export function ferramentaPorAtalho(tecla: string): Ferramenta | undefined {
  const alvo = normalizaTecla(tecla);
  for (const ferramenta of ferramentas.values()) {
    if (ferramenta.shortcut && normalizaTecla(ferramenta.shortcut) === alvo) return ferramenta;
  }
  return undefined;
}

/** Atalhos não disparam com Ctrl/Alt/Meta (são do navegador) nem com o foco num campo de texto. */
export function atalhoBloqueado(
  evento: Pick<KeyboardEvent, 'ctrlKey' | 'altKey' | 'metaKey'>,
  foco: { tagName: string; isContentEditable?: boolean } | null,
): boolean {
  if (evento.ctrlKey || evento.altKey || evento.metaKey) return true;
  if (!foco) return false;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(foco.tagName) || foco.isContentEditable === true;
}

/** Monta a barra dentro do #stage e registra as ferramentas nativas (as que outro módulo já registrou ficam). */
export function montaBarra(acoes: AcoesBarra): void {
  const stage = document.getElementById('stage');
  if (barra || !stage) return;
  leTema();
  barra = document.createElement('nav');
  barra.id = 'barra-ferramentas';
  barra.className = 'barra-ferramentas';
  for (const grupo of ['vista', 'paineis'] as const) {
    const el = document.createElement('div');
    el.className = 'grupo-ferramentas';
    el.dataset.grupo = grupo;
    el.setAttribute('role', 'group');
    grupos.set(grupo, el);
    barra.append(el);
  }
  stage.append(barra);
  acoesMontadas = acoes;
  for (const ferramenta of ferramentasNativas(acoes)) {
    if (ferramentas.has(ferramenta.id)) continue;
    minhas.set(ferramenta.id, ferramenta);
    registerTool(ferramenta);
  }
  document.addEventListener('keydown', aoTecla);
  onLangChange(aoTrocarIdioma);
  aoTrocarIdioma();
}

function criaBotao(id: string): HTMLButtonElement {
  const botao = document.createElement('button');
  botao.type = 'button';
  botao.className = 'ferramenta';
  botao.dataset.id = id;
  botao.addEventListener('click', () => {
    ferramentas.get(id)?.onClick();
    refreshTools();
  });
  return botao;
}

function desenhaRotulo(botao: HTMLButtonElement, ferramenta: Ferramenta): void {
  const nome = ferramenta.shortcut ? `${ferramenta.label} (${ferramenta.shortcut})` : ferramenta.label;
  botao.dataset.dica = nome;
  botao.setAttribute('aria-label', nome);
  botao.innerHTML = `${ferramenta.icon}<span class="selo-ferramenta" hidden></span>`;
}

function pintaEstado(botao: HTMLButtonElement, ferramenta: Ferramenta): void {
  const ativo = ferramenta.active?.() ?? false;
  botao.classList.toggle('ativo', ativo);
  if (ferramenta.active) botao.setAttribute('aria-pressed', String(ativo));
  else botao.removeAttribute('aria-pressed');
  const selo = botao.querySelector<HTMLElement>('.selo-ferramenta');
  const texto = textoSelo(ferramenta.badge?.() ?? 0);
  if (selo) {
    selo.textContent = texto;
    selo.hidden = texto === '';
  }
}

/** Textos da barra e das ferramentas nativas, refeitos ao trocar o idioma. */
function aoTrocarIdioma(): void {
  const acoes = acoesMontadas;
  if (!barra || !acoes) return;
  barra.setAttribute('aria-label', t('barra.ferramentas'));
  grupos.get('vista')?.setAttribute('aria-label', t('barra.grupo_vista'));
  grupos.get('paineis')?.setAttribute('aria-label', t('barra.grupo_paineis'));
  for (const ferramenta of ferramentasNativas(acoes)) {
    // só troca o que ainda é nosso: se outro módulo registrou o mesmo id, ele manda
    if (ferramentas.get(ferramenta.id) !== minhas.get(ferramenta.id)) continue;
    minhas.set(ferramenta.id, ferramenta);
    ferramentas.set(ferramenta.id, ferramenta);
  }
  refreshTools();
}

function aoTecla(evento: KeyboardEvent): void {
  // a tela de login fica por cima do escritório: sem sessão, nenhum atalho
  const app = document.getElementById('app');
  if (!app || app.hidden) return;
  if (atalhoBloqueado(evento, document.activeElement as HTMLElement | null)) return;
  const ferramenta = ferramentaPorAtalho(evento.key);
  if (!ferramenta) return;
  evento.preventDefault();
  ferramenta.onClick();
  refreshTools();
}

/** Os botões da barra. Meu dia, Entregas e Histórico só avisam (agzos:painel): quem mostra o painel escuta. */
function ferramentasNativas(acoes: AcoesBarra): Ferramenta[] {
  const pedePainel = (painel: 'dia' | 'entregas' | 'historico') => window.dispatchEvent(new CustomEvent('agzos:painel', { detail: painel }));
  return [
    { id: 'visao', icon: ICONE.visao, label: t('barra.visao'), shortcut: 'O', group: 'vista', onClick: () => acoes.campus.fitAll() },
    { id: 'afastar', icon: ICONE.afastar, label: t('barra.afastar'), shortcut: '−', group: 'vista', onClick: () => acoes.campus.zoomOut() },
    { id: 'aproximar', icon: ICONE.aproximar, label: t('barra.aproximar'), shortcut: '+', group: 'vista', onClick: () => acoes.campus.zoomIn() },
    { id: 'dia', icon: ICONE.dia, label: t('topo.meu_dia'), shortcut: 'M', group: 'paineis', onClick: () => pedePainel('dia'), active: () => acoes.painel() === 'day' },
    { id: 'entregas', icon: ICONE.entregas, label: t('barra.entregas'), shortcut: 'E', group: 'paineis', onClick: () => pedePainel('entregas') },
    { id: 'historico', icon: ICONE.historico, label: t('barra.historico'), shortcut: 'H', group: 'paineis', onClick: () => pedePainel('historico') },
    { id: 'timelapse', icon: ICONE.timelapse, label: t('dia.timelapse'), group: 'paineis', onClick: () => acoes.timelapse(), active: () => acoes.timelapseAberto() },
    { id: 'terminal', icon: ICONE.terminal, label: t('topo.terminal'), group: 'paineis', onClick: () => acoes.terminal(), active: () => acoes.painel() === 'terminal' },
    { id: 'tema', icon: ICONE.tema, label: t('barra.tema'), group: 'paineis', onClick: trocaTema, active: temaClaro },
    // o "?" continua com main.ts (que já trata a tecla); aqui ele só aparece no nome
    { id: 'ajuda', icon: ICONE.ajuda, label: `${t('barra.ajuda')} (?)`, group: 'paineis', onClick: () => acoes.ajuda(), active: () => acoes.ajudaAberta() },
  ];
}

// ------------------------------------------------------------------ tema da tela

const TEMA_KEY = 'agzos.tema';

function temaClaro(): boolean {
  return document.body.dataset.tema === 'claro';
}

/** Escuro é o padrão. Sem localStorage (modo privado), vale só nesta aba. */
function leTema(): void {
  let salvo: string | null = null;
  try {
    salvo = globalThis.localStorage?.getItem(TEMA_KEY) ?? null;
  } catch {
    /* sem persistência */
  }
  document.body.dataset.tema = salvo === 'claro' ? 'claro' : 'escuro';
}

function trocaTema(): void {
  const claro = !temaClaro();
  document.body.dataset.tema = claro ? 'claro' : 'escuro';
  try {
    globalThis.localStorage?.setItem(TEMA_KEY, claro ? 'claro' : 'escuro');
  } catch {
    /* sem persistência: vale só nesta aba */
  }
  refreshTools();
}

// ------------------------------------------------------------------ ícones (pixel art em traço grosso)

const svg = (corpo: string) => `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${corpo}</svg>`;

const ICONE = {
  visao: svg('<path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5"/><rect x="9" y="9" width="6" height="6"/>'),
  afastar: svg('<circle cx="10.5" cy="10.5" r="6"/><path d="M15 15l5 5M7.5 10.5h6"/>'),
  aproximar: svg('<circle cx="10.5" cy="10.5" r="6"/><path d="M15 15l5 5M7.5 10.5h6M10.5 7.5v6"/>'),
  dia: svg('<path d="M3 21h18M6 17v-5M11 17V7M16 17v-8"/>'),
  entregas: svg('<rect x="3" y="7" width="18" height="14"/><path d="M3 11h18M12 13.5v5M9.5 16h5"/>'),
  historico: svg('<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/>'),
  timelapse: svg('<path d="M4 12a8 8 0 1 0 2.5-5.8"/><path d="M3 4v4.5h4.5"/><path d="M12 8v4.5l3 2"/>'),
  terminal: svg('<rect x="3" y="4" width="18" height="16"/><path d="M7 9l3 3-3 3M13 15h4"/>'),
  // sol aparece no tema escuro (vai para o claro); lua, no claro (vai para o escuro): o CSS escolhe
  tema: svg(
    '<g class="ic-sol"><circle cx="12" cy="12" r="4"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2"/></g>' +
      '<g class="ic-lua"><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z"/></g>',
  ),
  ajuda: svg('<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5v.7M12 17.5v.5"/>'),
};
